import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard,
  CalendarDays,
  History,
  PlusCircle,
  CheckSquare,
  Users,
  LogOut,
  Menu,
  Palmtree,
  FileText,
  AlertTriangle,
  FileCheck,
  BookOpen,
  Clock,
  Megaphone,
  Download,
  Smartphone,
  Heart,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { NotificationCenter } from "@/components/NotificationCenter";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["empleado", "supervisor", "administrador", "rrhh"] },
  { path: "/nueva-solicitud", label: "Nueva Solicitud", icon: PlusCircle, roles: ["empleado", "supervisor", "administrador", "rrhh"] },
  { path: "/historial", label: "Historial Vacaciones", icon: History, roles: ["empleado", "supervisor", "administrador", "rrhh"] },
  { path: "/calendario", label: "Calendario", icon: CalendarDays, roles: ["empleado", "supervisor", "administrador", "rrhh"] },
  { path: "/aprobaciones", label: "Aprobaciones", icon: CheckSquare, roles: ["supervisor", "administrador", "rrhh"], permiso: "puede_aprobar" },
  { path: "/equipo-dashboard", label: "Estatus de Equipo", icon: Users, roles: ["supervisor", "administrador", "rrhh"] },
  
  // Nuevos Módulos
  { path: "/constancias", label: "Constancias", icon: FileText, roles: ["empleado", "supervisor", "administrador", "rrhh"] },
  { path: "/boletos-ornato", label: "Boletos de Ornato", icon: FileCheck, roles: ["empleado", "supervisor", "administrador", "rrhh"] },
  { path: "/manuales", label: "Manuales & XP", icon: BookOpen, roles: ["empleado", "supervisor", "administrador", "rrhh"] },
  { path: "/beneficios", label: "Corazón Naranja", icon: Heart, roles: ["empleado", "supervisor", "administrador", "rrhh"] },
  { path: "/llamadas-atencion", label: "Llamadas de Atención", icon: AlertTriangle, roles: ["empleado", "supervisor", "administrador", "rrhh"] },
  { path: "/marcajes", label: "Control de Marcajes", icon: Clock, roles: ["administrador", "rrhh"], permiso: "puede_marcajes" },
  { path: "/comunicados", label: "Comunicados RRHH", icon: Megaphone, roles: ["administrador", "rrhh"] },
  
  { path: "/admin", label: "Administración", icon: Users, roles: ["administrador", "rrhh"] },
];

const NavItem = ({ item, onClick }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all text-sm font-medium ${
          isActive
            ? "bg-orange-500 text-white shadow-sm shadow-orange-500/20"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`
      }
    >
      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
};

const Sidebar = ({ user, onNavClick }) => {
  // Un colaborador ve la opción por su rol o por un permiso individual (jefes, encargados)
  const filteredItems = navItems.filter((item) =>
    item.roles.includes(user?.rol) || (item.permiso && user?.[item.permiso])
  );

  return (
    <div className="flex flex-col h-full bg-white select-none">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-100 shrink-0">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm overflow-hidden bg-white p-1 border border-slate-100">
          <img src="/logo-cps.png" alt="CPS Logistics" className="w-full h-full object-contain" />
        </div>
        <div className="min-w-0">
          <h1 className="font-bold text-slate-800 text-base leading-tight truncate">CPS Time Off</h1>
          <p className="text-[11px] text-slate-500 truncate">Portal de Gestión & RRHH</p>
        </div>
      </div>

      {/* Navigation con scroll vertical */}
      <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto overscroll-contain">
        {filteredItems.map((item) => (
          <NavItem key={item.path} item={item} onClick={onNavClick} />
        ))}
      </nav>

      {/* User Info */}
      <div className="px-4 py-3.5 border-t border-slate-100 bg-slate-50/50 shrink-0">
        <div className="flex items-center gap-2.5 px-1">
          <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse"></div>
          <div className="min-w-0">
            <span className="text-xs font-semibold text-slate-700 capitalize block truncate">{user?.nombre}</span>
            <span className="text-[10px] text-slate-400 capitalize block truncate">{user?.rol}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [hasUpdate, setHasUpdate] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                setHasUpdate(true);
                toast.info("¡Nueva versión de CPS Time Off disponible!", {
                  description: "Haz clic en el botón 'Actualizar' para cargar los cambios.",
                });
              }
            });
          }
        });
      });
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setDeferredPrompt(null);
    }
  };

  const handleCheckUpdate = async () => {
    setCheckingUpdate(true);
    try {
      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          await reg.update();
          if (reg.waiting) {
            reg.waiting.postMessage({ type: "SKIP_WAITING" });
          }
        }
      }
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
      toast.success("Actualizando y recargando a la versión más reciente...");
      setTimeout(() => {
        window.location.reload(true);
      }, 700);
    } catch (err) {
      window.location.reload(true);
    } finally {
      setTimeout(() => setCheckingUpdate(false), 2500);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getInitials = (nombre, apellido) => {
    return `${nombre?.[0] || ""}${apellido?.[0] || ""}`.toUpperCase();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 hidden lg:block z-40">
        <Sidebar user={user} />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-[290px] sm:w-80 max-w-[85vw]">
              <Sidebar user={user} onNavClick={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden bg-white p-0.5 border border-slate-100 shadow-sm">
              <img src="/logo-cps.png" alt="CPS" className="w-full h-full object-contain" />
            </div>
            <span className="font-bold text-slate-800 text-sm">CPS Time Off</span>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {deferredPrompt && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleInstallClick}
              title="Instalar App PWA"
              className="h-8 px-2 text-xs text-orange-600 border-orange-200 hover:bg-orange-50 font-medium gap-1"
            >
              <img src="/logo-cps.png" alt="Logo" className="w-3.5 h-3.5 object-contain" />
              Instalar
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={handleCheckUpdate}
            disabled={checkingUpdate}
            title={hasUpdate ? "¡Nueva versión disponible! Pulsa para actualizar" : "Actualizar versión / Refrescar aplicación"}
            className={`h-8 w-8 relative ${hasUpdate ? "text-emerald-600 bg-emerald-50" : "text-slate-600 hover:text-slate-900"}`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${checkingUpdate ? "animate-spin text-orange-500" : ""}`} />
            {hasUpdate && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-emerald-500 rounded-full animate-ping" />
            )}
          </Button>

          <NotificationCenter />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-10 w-10 rounded-full p-0 ml-1">
                <Avatar className="h-9 w-9 bg-orange-100">
                  <AvatarFallback className="bg-orange-100 text-orange-600 font-medium text-xs">
                    {getInitials(user?.nombre, user?.apellido)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{user?.nombre} {user?.apellido}</span>
                  <span className="text-xs text-slate-500 font-normal">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar Sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        {/* Desktop Header */}
        <header className="hidden lg:flex h-16 bg-white border-b border-slate-200 items-center justify-between px-8">
          <div>
            <p className="text-slate-600">
              Bienvenido, <span className="font-medium text-slate-800">{user?.nombre}</span>
            </p>
          </div>

          <div className="flex items-center gap-3">
            {deferredPrompt && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleInstallClick}
                className="text-orange-600 border-orange-200 hover:bg-orange-50 text-xs font-semibold gap-2 shadow-sm"
              >
                <img src="/logo-cps.png" alt="Logo" className="w-4 h-4 object-contain" />
                Instalar App en este equipo
              </Button>
            )}

            <Button
              variant={hasUpdate ? "default" : "outline"}
              size="sm"
              onClick={handleCheckUpdate}
              disabled={checkingUpdate}
              title="Buscar actualizaciones y recargar la última versión del sistema"
              className={`text-xs font-semibold gap-1.5 shadow-sm transition-all ${
                hasUpdate
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white animate-pulse"
                  : "text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-800"
              }`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${checkingUpdate ? "animate-spin text-orange-500" : ""}`} />
              {hasUpdate ? "¡Nueva Versión Disponible!" : (checkingUpdate ? "Comprobando..." : "Actualizar App")}
            </Button>

            <NotificationCenter />

            <div className="h-6 w-px bg-slate-200" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-3 h-auto py-2 px-3">
                  <Avatar className="h-9 w-9 bg-orange-100">
                    <AvatarFallback className="bg-orange-100 text-orange-600 font-medium text-sm">
                      {getInitials(user?.nombre, user?.apellido)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="text-sm font-medium text-slate-800">
                      {user?.nombre} {user?.apellido}
                    </p>
                    <p className="text-xs text-slate-500 capitalize">{user?.rol}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.nombre} {user?.apellido}</span>
                    <span className="text-xs text-slate-500 font-normal">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full min-h-[calc(100vh-4rem)]">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
